import { useEffect, useMemo, useRef, useState } from 'react'
import {
  keepPreviousData,
  useInfiniteQuery,
  useQueryClient,
  UseInfiniteQueryResult,
  InfiniteData,
} from '@tanstack/react-query'
import { FetchParams, PaginatedResponse } from './types'

type PaginateResult<T> = UseInfiniteQueryResult<InfiniteData<PaginatedResponse<T>>, Error> & {
  flatData: T[]
  totalCount: number
  pageMetadata: PaginatedResponse<T>['metadata'] | null
  /**
   * The list on screen is known to be current since this screen showed it:
   * either it was fetched, or a `pollMs` check found it unchanged. A list read
   * from the cache is not, until then.
   */
  isUpToDate: boolean
}
type Fetcher<T> = (params: FetchParams) => Promise<PaginatedResponse<T>>
// `keepPreviousData` keeps the last successful result on screen while a new
// one loads (e.g. switching a filter tab or a background refetch triggered by
// a push notification), so the list never flashes blank. Opt-in per caller to
// avoid changing the behaviour of every paginated list at once.
//
// `pollMs` checks at that interval whether the list changed, for data that
// someone else changes (a message received): no local mutation tells the app
// about it. Also opt-in; pass `false` while the screen is not in front to
// pause it. A check asks for the first page only, whatever the number of pages
// already loaded; the whole list is asked again only when that page changed.
//
// `pollSignature` says what "changed" means: two first pages with the same
// signature are the same list. Without it the whole page is compared.
//
// `nextPageWhenFull`: some routes of the API in production put the size of the
// page in `metadata.count`, so that total cannot tell whether more follows.
// With this option a full page means "ask for the next one" (at worst one
// empty request when the total is an exact multiple of the page size).
//
// `abortWhenReplaced` hands the fetcher a `signal` that aborts when no screen
// shows this list any more (the search text changed, the screen closed): the
// request is dropped and the list goes back to what it held. Opt-in, for
// searches that cost the server a lot.
type UsePaginateOptions<T> = {
  enabled?: boolean
  abortWhenReplaced?: boolean
  nextPageWhenFull?: boolean
  keepPreviousData?: boolean
  pollMs?: number | false
  pollSignature?: (firstPage: PaginatedResponse<T>) => string
}

export function usePaginate<T>(
  key: string,
  fetcher: Fetcher<T>,
  extraParams?: Record<string, any>,
  options?: UsePaginateOptions<T>,
): PaginateResult<T> {
  const queryClient = useQueryClient()
  const query = useInfiniteQuery<PaginatedResponse<T>, Error>({
    queryKey: [key, extraParams],
    // Reading `signal` is what makes the request abortable: left alone otherwise.
    queryFn: (context) =>
      fetcher({
        pageParam: context.pageParam as string | undefined,
        ...extraParams,
        ...(options?.abortWhenReplaced && { signal: context.signal }),
      }),
    getNextPageParam: (lastPage: PaginatedResponse<T>) => {
      if (options?.nextPageWhenFull) {
        const limit = Number(lastPage?.metadata?.limit ?? 0)
        const page = Number(lastPage?.metadata?.page ?? 0)
        return limit > 0 && (lastPage?.data?.length ?? 0) >= limit ? page + 1 : undefined
      }
      const { count, limit, page } = lastPage?.metadata ?? { count: 0, limit: 0, page: 0 }
      const totalPages = Math.ceil(count / limit) - 1
      return page <= totalPages ? page + 1 : undefined
    },
    initialPageParam: '1',
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
    ...(options?.keepPreviousData && { placeholderData: keepPreviousData }),
  })

  // Kept in refs: callers pass new functions at each render, and the interval
  // must not restart for that.
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  const signatureRef = useRef(options?.pollSignature)
  signatureRef.current = options?.pollSignature

  const pollMs = options?.pollMs || false
  const paramsKey = JSON.stringify(extraParams)
  const listId = `${key}|${paramsKey}`
  const [checkedListId, setCheckedListId] = useState<string>()
  // Forgotten when another list is shown (not when polling merely pauses): a
  // list left and reopened from the cache is not current until checked again.
  useEffect(() => () => setCheckedListId(undefined), [listId])
  useEffect(() => {
    if (!pollMs) return
    const queryKey = [key, extraParams]
    const signatureOf = (page: PaginatedResponse<T> | undefined) =>
      page ? (signatureRef.current ?? JSON.stringify)(page) : undefined
    let stopped = false
    let checking = false

    const check = async () => {
      const before = queryClient.getQueryState<InfiniteData<PaginatedResponse<T>>>(queryKey)
      // Nothing to compare with yet, or the list is already being asked for.
      if (checking || !before?.data || before.fetchStatus !== 'idle') return
      checking = true
      try {
        const head = await fetcherRef.current({ pageParam: '1', ...extraParams })
        const after = queryClient.getQueryState<InfiniteData<PaginatedResponse<T>>>(queryKey)
        // Refreshed meanwhile (a message sent, a push): it is newer than `head`.
        if (stopped || !after?.data || after.dataUpdatedAt !== before.dataUpdatedAt) return
        if (signatureOf(head) !== signatureOf(after.data.pages[0])) {
          await queryClient.invalidateQueries({ queryKey, exact: true })
        }
        if (!stopped) setCheckedListId(listId)
      } catch {
        // A check that fails is simply tried again at the next interval.
      } finally {
        checking = false
      }
    }

    // Catch up at once each time polling starts or resumes (screen back in
    // front, app back to the foreground, another list opened): the cached list
    // counts as fresh for minutes and the first tick is a full interval away.
    void check()
    const interval = setInterval(() => void check(), pollMs)
    return () => {
      stopped = true
      clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollMs, key, paramsKey, queryClient])

  // flatten pages for easier FlatList use. Same array for as long as the pages
  // are the same, so a screen that memoises on it does not recompute at each render.
  const pages = query.data?.pages
  const flatData = useMemo(() => pages?.flatMap((page) => page.data ?? []) ?? [], [pages])
  // Get total count from the first page's metadata (count is the same across all pages)
  const totalCount = query.data?.pages[0]?.metadata?.count ?? 0
  const pageMetadata = query.data?.pages[0]?.metadata ?? null

  return {
    ...query,
    flatData,
    totalCount,
    pageMetadata,
    isUpToDate: query.isFetchedAfterMount || checkedListId === listId,
  }
}
